import React, { useState } from "react";
import { Send, CheckCircle, AlertCircle } from "lucide-react";

export default function ContactForm({ subject = "Generel henvendelse" }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    subject: subject,
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Der skete en fejl ved afsendelse. Prøv venligst igen.");
      }

      setStatus({ submitting: false, success: true, error: null });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        subject: subject,
        website_url: "",
      });
    } catch (err) {
      console.error("Form submission error:", err);
      setStatus({ submitting: false, success: false, error: err.message });
    }
  };

  return (
    <div className="bg-white p-8 border border-neutral-200 shadow-sm">
      <h3 className="text-xl font-serif font-bold text-neutral-900 mb-6">
        Send os en besked
      </h3>

      {status.success ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 flex items-start gap-3 mb-6">
          <CheckCircle className="text-emerald-600 shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-semibold text-sm">Beskeden er sendt!</h4>
            <p className="text-xs text-emerald-700 mt-1">
              Tak for din henvendelse. Vi vender tilbage til dig så hurtigt som muligt.
            </p>
          </div>
        </div>
      ) : null}

      {status.error ? (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-6 flex items-start gap-3 mb-6">
          <AlertCircle className="text-rose-600 shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-semibold text-sm">Der opstod en fejl</h4>
            <p className="text-xs text-rose-700 mt-1">{status.error}</p>
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
        <div className="absolute sr-only overflow-hidden w-0 h-0" aria-hidden="true">
          <label htmlFor="website_url">Do not fill this out if you are human</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            Navn <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-neutral-200 rounded-none text-sm focus:outline-none focus:border-neutral-900 transition-colors"
            placeholder="Dit fulde navn"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
              E-mail <span className="text-rose-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-neutral-200 rounded-none text-sm focus:outline-none focus:border-neutral-900 transition-colors"
              placeholder="din@email.dk"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
              Telefon <span className="text-rose-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-neutral-200 rounded-none text-sm focus:outline-none focus:border-neutral-900 transition-colors"
              placeholder="Tlf. nr."
            />
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            Besked <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows="5"
            value={formData.message}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-neutral-200 rounded-none text-sm focus:outline-none focus:border-neutral-900 transition-colors resize-none"
            placeholder="Skriv din besked eller bestilling her..."
          />
        </div>

        <button
          type="submit"
          disabled={status.submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs font-semibold uppercase tracking-wider bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status.submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Sender...</span>
            </>
          ) : (
            <>
              <Send size={14} />
              <span>Send besked</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
